import { useState, useMemo } from 'react';
import { Card, Button, Badge, Modal, message, Space, Divider, Row, Col, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faEdit, faTrash, faPlus, faUser, faBookOpen } from '@fortawesome/free-solid-svg-icons';
import { AsistenciaForm } from './AsistenciaForm';
const { Meta } = Card;
const { Title, Text } = Typography;

export function AsistenciasCard({
  open,
  onClose,
  participanteId,
  participantes,
  tematicas,
  onCreateAsistencia,
  onUpdateAsistencia,
  onDeleteAsistencia,
  loading,
  permissionInsert,
  permissionDelete,
  permissionUpdate
}) {
  const [showAsistenciaForm, setShowAsistenciaForm] = useState(false);
  const [editingAsistencia, setEditingAsistencia] = useState(null);

  const participante = useMemo(() => {
    return participantes.find(p => p.oid === participanteId);
  }, [participantes, participanteId]);

  if (!participante) return null;

  const asistencias = participante.children || [];

  const handleCreateAsistencia = () => {
    setEditingAsistencia(null);
    setShowAsistenciaForm(true);
  };

  const handleEditAsistencia = (asistencia) => {
    setEditingAsistencia(asistencia);
    setShowAsistenciaForm(true);
  };

  const handleSaveAsistencia = async (asistenciaData) => {
    try {
      if (editingAsistencia) {
        const success = await onUpdateAsistencia(editingAsistencia.oid, asistenciaData);
        if (success) {
          message.success('Asistencia actualizada correctamente');
        }
      } else {
        await onCreateAsistencia(asistenciaData);
        message.success('Asistencia creada correctamente');
      }
      setShowAsistenciaForm(false);
      setEditingAsistencia(null);
    } catch (error) {
      console.error('Error al guardar asistencia:', error);
    }
  };

  const handleDeleteAsistencia = (asistencia) => {
    Modal.confirm({
      title: '¿Estás seguro de que deseas eliminar esta asistencia?',
      content: 'Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await onDeleteAsistencia(asistencia.oid, participante.oid);
          message.success('Asistencia eliminada correctamente');
        } catch (error) {
          console.error('Error al eliminar asistencia:', error);
          message.error('Error al eliminar asistencia');
        }
      }
    });
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        width="80%"
        style={{ top: 20 }}
        footer={null}
        title={
          <Space>
            <FontAwesomeIcon icon={faUser} />
            <span style={{ fontWeight: 'bold', fontSize: 20, }}>Asistencias de {participante.nombre_matriculado}</span>
          </Space>
        }
      >
        <div style={{ padding: '24px' }}>
          <Card
            variant="outlined"
            style={{
              border: '1px solid #eee',
              marginBottom: 24,
              backgroundColor: '#fafafa',
              borderRadius: 8,
              textAlign: 'center',
            }}
          >
            <Row gutter={[16, 16]} >
              <Col xs={24} sm={8}>
                <Text type="secondary" style={{ display: 'block', fontSize: 20 }}>Identificación</Text>
                <Title level={5} style={{ margin: 0, fontSize: 18 }}>{participante.identificacion}</Title>
              </Col>
              <Col xs={24} sm={8}>
                <Text type="secondary" style={{ display: 'block', fontSize: 20 }}>Cohorte</Text>
                <Badge
                  status="processing"
                  style={{ marginTop: 4 }}
                  text={<span style={{ fontSize: 18 }}>{participante.cohorte}</span>}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Text type="secondary" style={{ display: 'block', fontSize: 20 }}>Estado</Text>
                <Badge
                  status={participante.activo === 'Si' ? 'success' : 'error'}
                  text={
                    <span style={{ fontSize: 18 }}>
                      {participante.activo === 'Si' ? 'Activo' : 'Inactivo'}
                    </span>
                  }
                  style={{ marginTop: 4 }}
                />
              </Col>
            </Row>
          </Card>
          <Divider style={{ margin: '16px 0' }} />
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 16
            }}>
              <Title level={4} style={{ margin: 0 }}>
                <Space>
                  <FontAwesomeIcon icon={faBookOpen} />
                  <span>Asistencias ({asistencias.length})</span>
                </Space>
              </Title>
              {permissionInsert && (
                <Button
                  type="primary"
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={handleCreateAsistencia}
                  size="middle"
                >
                  Nueva Asistencia
                </Button>
              )}

            </div>
            {asistencias.length === 0 ? (
              <Card
                variant="outlined"
                style={{
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                  borderRadius: 8,
                }}
              >
                <FontAwesomeIcon
                  icon={faBookOpen}
                  style={{
                    marginTop: 16,
                    fontSize: 48,
                    color: '#d9d9d9',
                    marginBottom: 16
                  }}
                />
                <Text type="secondary" style={{ display: 'block', marginBottom: 24, color: '#d9d9d9' }}>
                  Este participante no tiene asistencias registradas
                </Text>
              </Card>
            ) : (
              <Row gutter={[16, 16]}>
                {asistencias.map((asistencia) => (
                  <Col key={asistencia.oid} xs={24} sm={12} md={8} lg={8}>
                    <Card
                      hoverable
                      style={{
                        borderRadius: 12,
                        height: '100%',
                        padding: 15,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                      styles={{
                        body: {
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          padding: 0
                        }
                      }}
                    >

                      <div style={{ flex: 1 }}>
                        <Meta
                          title={
                            <Row justify="space-between" align="middle">
                              <Col span={16}>
                                <Text strong ellipsis style={{ fontSize: 20 }}>
                                  {asistencia.nombre_tematica}
                                </Text>
                              </Col>
                              <Col>
                                <Badge
                                  status={asistencia.participa === 1 ? 'success' : 'error'}
                                  text={asistencia.participa === 1 ? 'Participó' : 'No participó'}
                                />
                              </Col>
                            </Row>
                          }
                          description={
                            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                              <Row align="middle" gutter={12}>
                                <Col>
                                  <FontAwesomeIcon icon={faCalendar} style={{ color: '#8c8c8c' }} />
                                </Col>
                                <Col>
                                  <Text type="secondary">
                                    Fecha: {new Date(asistencia.fecha).toLocaleDateString('es-ES', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric'
                                    })}
                                  </Text>
                                </Col>
                              </Row>
                            </Space>
                          }
                        />
                      </div>
                      <div style={{
                        marginTop: 'auto',
                        paddingTop: 16
                      }}>


                        <Row gutter={12} justify="center" align="middle">
                          {permissionUpdate && (
                            <Col span={9}>
                              <Button
                                type="primary"
                                icon={<FontAwesomeIcon icon={faEdit} />}
                                block
                                style={{ height: 35 }}
                                onClick={() => handleEditAsistencia(asistencia)}
                              >
                                Editar
                              </Button>
                            </Col>)}
                          {permissionDelete && (
                            <Col span={9}>
                              <Button
                                type="default"
                                icon={<FontAwesomeIcon icon={faTrash} />}
                                danger
                                block
                                style={{ height: 35 }}
                                onClick={() => handleDeleteAsistencia(asistencia)}
                              >
                                Eliminar
                              </Button>
                            </Col>)}
                        </Row>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Space>
        </div>
      </Modal>
      <AsistenciaForm
        open={showAsistenciaForm}
        onClose={() => {
          setShowAsistenciaForm(false);
          setEditingAsistencia(null);
        }}
        onSave={handleSaveAsistencia}
        asistencia={editingAsistencia}
        participante={participante}
        tematicas={tematicas}
        loading={loading}
      />
    </>
  );
}